import React, { useContext, useEffect, useRef, useState } from 'react';
import { Avatar, Button, Form, Input, Spin, message } from 'antd';
import { ArrowLeft, Camera, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/authContext';
import { useCompany } from '../../context/companyContext';
import { useProfile } from '../../context/profileContext';
import axiosInstance from '../../config/axiosConfig';
import './ProfileSettings.css';

const formatLabel = (value, fallback = 'Not set') => {
  if (!value) return fallback;
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getInitials = (value) => {
  const words = String(value || 'C').trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w.charAt(0).toUpperCase()).join('') || 'C';
};

const ProfileSettings = () => {
  useContext(AuthContext); // keep context active for auth token
  const { loadCompanyLogo } = useCompany();
  const { profile, profileLoading, refreshProfile } = useProfile();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pwForm] = Form.useForm();
  const [changingPw, setChangingPw] = useState(false);
  const fileInputRef = useRef(null);

  // Sync form + avatar whenever profile data arrives from context
  useEffect(() => {
    if (!profile) return;
    setAvatarSrc(profile.logo_url || null);
    form.setFieldsValue({ companyname: profile.companyname || '' });
  }, [profile, form]);

  // Dirty tracking — used to enable/disable Discard button
  const watchedName = Form.useWatch('companyname', form);
  const isDirty = (watchedName !== undefined && watchedName !== (profile?.companyname || '')) || !!pendingFile;

  const copyCompanyId = async () => {
    const id = profile?.companyid;
    if (!id) return;
    try {
      await navigator.clipboard?.writeText(id);
      message.success('Company ID copied');
    } catch {
      message.error('Could not copy');
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      // 1. Update company name if changed
      if (values.companyname !== profile?.companyname) {
        await axiosInstance.patch('/company/profile/name', { companyname: values.companyname });
      }

      // 2. Upload logo if a new file was selected
      if (pendingFile) {
        const formData = new FormData();
        formData.append('logo', pendingFile);
        await axiosInstance.post('/company/profile/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPendingFile(null);
      }

      message.success('Changes saved');
      // Refresh context — DashboardLayout will react automatically
      await refreshProfile();
      loadCompanyLogo();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!profile) return;
    form.setFieldsValue({ companyname: profile.companyname || '' });
    setAvatarSrc(profile.logo_url || null);
    setPendingFile(null);
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.error('Please select a valid image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('Image must be under 5 MB');
      return;
    }
    setPendingFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarSrc(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleChangePassword = async () => {
    const values = await pwForm.validateFields();
    setChangingPw(true);
    try {
      await axiosInstance.post('/company/profile/change-password', {
        current_password: values.currentPassword,
        new_password:     values.newPassword,
      });
      message.success('Password changed successfully');
      pwForm.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  const initials = getInitials(profile?.companyname);

  if (profileLoading && !profile) {
    return (
      <div className="ps-page ps-page--loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <main className="ps-page">
      {/* ── Page Header ── */}
      <div className="ps-page-header">
        <div className="ps-page-header-left">
          <button type="button" className="ps-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
            <span>Back</span>
          </button>
          <h1 className="ps-page-title">Company Profile</h1>
          <p className="ps-page-subtitle">Manage your company information</p>
        </div>
        <div className="ps-page-header-right">
          <Button className="ps-btn-discard" onClick={handleReset} disabled={!isDirty}>
            Discard
          </Button>
          <Button type="primary" className="ps-btn-save" loading={saving} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
      <hr className="ps-page-rule" />

      {/* ── Layout ── */}
      <div className="ps-layout">
        {/* Left — Company Profile */}
        <div className="ps-col-main">
          <div className="ps-card ps-card--profile">

            {/* Identity banner */}
            <div className="ps-identity">
              <div
                className="ps-avatar-clickable"
                onClick={handleAvatarClick}
                role="button"
                tabIndex={0}
                aria-label="Upload company logo"
                onKeyDown={(e) => e.key === 'Enter' && handleAvatarClick()}
              >
                <Avatar
                  className={avatarSrc ? 'ps-avatar ps-avatar--img' : 'ps-avatar'}
                  size={80}
                  src={avatarSrc || undefined}
                >
                  {initials}
                </Avatar>
                <div className="ps-avatar-overlay">
                  <Camera size={16} strokeWidth={2} />
                </div>
              </div>

              <div className="ps-identity-text">
                <h2 className="ps-identity-name">{profile?.companyname || 'Company'}</h2>
                <span className="ps-role-badge ps-role-badge--muted">
                  {formatLabel(profile?.company_type, 'Standard')}
                </span>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="ps-file-input"
                onChange={handleFileChange}
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>

            {/* Editable fields */}
            <div className="ps-card-body">
              <Form form={form} layout="vertical" className="ps-form">
                <Form.Item
                  label="Company name"
                  name="companyname"
                  rules={[{ required: true, message: 'Enter company name' }]}
                >
                  <Input size="large" placeholder="Acme Corp" />
                </Form.Item>
              </Form>

              {/* Logo change row */}
              <div className="ps-logo-row">
                <Avatar
                  className={avatarSrc ? 'ps-avatar ps-avatar--img ps-avatar--sm' : 'ps-avatar ps-avatar--sm'}
                  size={40}
                  src={avatarSrc || undefined}
                >
                  {initials}
                </Avatar>
                <div className="ps-logo-row-info">
                  <span className="ps-logo-row-label">Company logo</span>
                  <span className="ps-upload-hint">PNG or JPG · max 5 MB</span>
                </div>
                <button type="button" className="ps-change-btn" onClick={handleAvatarClick}>
                  Change
                </button>
              </div>

              {/* Contact info */}
              <div className="ps-section-divider" />
              <p className="ps-section-heading">Contact information</p>
              <div className="ps-contact-grid">
                <div className="ps-contact-item">
                  <span className="ps-contact-label">Email address</span>
                  <span className="ps-contact-value">{profile?.emailid || '—'}</span>
                </div>
                <div className="ps-contact-item">
                  <span className="ps-contact-label">Phone number</span>
                  <span className="ps-contact-value">
                    {profile?.countrycode && profile?.contactno
                      ? `+${profile.countrycode} ${profile.contactno}`
                      : profile?.contactno || '—'}
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Right sidebar */}
        <aside className="ps-col-side">

          {/* Account card */}
          <div className="ps-card">
            <h3 className="ps-card-title">Account</h3>
            <div className="ps-meta-row">
              <span className="ps-meta-label">Account type</span>
              <span className="ps-role-badge">
                {formatLabel(profile?.company_type, 'Standard')}
              </span>
            </div>
            <div className="ps-meta-col">
              <span className="ps-meta-label">Company ID</span>
              <div className="ps-id-box">
                <code>{profile?.companyid || 'Not available'}</code>
                <button
                  type="button"
                  className="ps-id-copy"
                  onClick={copyCompanyId}
                  aria-label="Copy company ID"
                  title="Copy"
                >
                  <Copy size={12} />
                </button>
              </div>
            </div>
          </div>

          {/* Security card */}
          <div className="ps-card">
            <h3 className="ps-card-title">Security</h3>
            <p className="ps-card-desc">Update your password to keep your account secure.</p>
            <Form form={pwForm} layout="vertical" className="ps-form">
              <Form.Item
                label="Current password"
                name="currentPassword"
                rules={[{ required: true, message: 'Enter current password' }]}
              >
                <Input.Password size="large" placeholder="Current password" />
              </Form.Item>
              <Form.Item
                label="New password"
                name="newPassword"
                rules={[
                  { required: true, message: 'Enter new password' },
                  { min: 8, message: 'Minimum 8 characters' },
                ]}
              >
                <Input.Password size="large" placeholder="Min. 8 characters" />
              </Form.Item>
              <Form.Item
                label="Confirm new password"
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password size="large" placeholder="Repeat new password" />
              </Form.Item>
              <Button
                type="primary"
                className="ps-btn-save ps-btn-full"
                loading={changingPw}
                onClick={handleChangePassword}
              >
                Update Password
              </Button>
            </Form>
          </div>

        </aside>
      </div>
    </main>
  );
};

export default ProfileSettings;
